"use client";

import React, { useState } from "react";
import Link from "next/link";
import { Breadcrumbs, BreadcrumbItem } from "./Breadcrumbs";
import {
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Sparkles,
  CreditCard,
  Truck,
  ShieldCheck,
  FileText,
  PhoneCall,
  Clock,
} from "lucide-react";

export interface WorkflowTimelineStep {
  step: string;
  title: string;
  duration: string;
  desc: string;
  deliverable: string;
}

export interface PaymentMethodItem {
  title: string;
  desc: string;
}

export interface ProcessWorkflowTemplateProps {
  breadcrumbs: BreadcrumbItem[];
  badgeText: string;
  title: string;
  description: string;
  steps: WorkflowTimelineStep[];
  paymentMethods: PaymentMethodItem[];
  guarantees: { title: string; desc: string }[];
  faqItems: { q: string; a: string }[];
  onOpenOrderModal: (service?: string) => void;
}

export function ProcessWorkflowTemplate({
  breadcrumbs,
  badgeText,
  title,
  description,
  steps,
  paymentMethods,
  guarantees,
  faqItems,
  onOpenOrderModal,
}: ProcessWorkflowTemplateProps) {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const toggleFaq = (idx: number) => {
    setOpenFaq(openFaq === idx ? null : idx);
  };

  return (
    <div className="pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Breadcrumbs items={breadcrumbs} />

        <div className="mt-4 rounded-3xl border border-gray-200/90 dark:border-zinc-800/90 bg-white dark:bg-[#0E121E] p-6 sm:p-10 relative overflow-hidden shadow-sm">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-gradient-to-br from-[#0a3d90]/15 to-[#55a9ef]/20 rounded-full blur-3xl pointer-events-none" />

          <div className="max-w-3xl relative z-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-950/40 border border-blue-200/60 dark:border-blue-800/40 text-[11px] font-mono uppercase tracking-wider text-[#0a3d90] dark:text-[#55a9ef] mb-4">
              <Sparkles className="w-3.5 h-3.5 text-[#FF2E93]" />
              <span>{badgeText}</span>
            </div>

            <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-gray-950 dark:text-white leading-[1.1]">
              {title}
            </h1>

            <p className="mt-4 text-base sm:text-lg text-gray-600 dark:text-zinc-300 leading-relaxed">
              {description}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => onOpenOrderModal("Порядок заказа")}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-bold text-white bg-gradient-to-r from-[#0a3d90] to-[#55a9ef] hover:from-[#083070] hover:to-[#4293d8] shadow-md transition-all active:scale-95"
              >
                <span>Оставить заявку</span>
                <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href="#steps"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold text-gray-800 dark:text-zinc-200 border border-gray-300 dark:border-zinc-700 hover:border-gray-950 dark:hover:border-zinc-300 hover:bg-gray-100 dark:hover:bg-zinc-800/60 transition-all"
              >
                <span>Смотреть шаги работы</span>
              </a>
            </div>
          </div>
        </div>

        <section id="steps" className="mt-16 sm:mt-20">
          <div className="max-w-2xl mb-10">
            <span className="text-xs font-mono font-bold uppercase tracking-wider text-[#FF2E93]">
              Прозрачный процесс
            </span>
            <h2 className="text-2xl sm:text-3xl font-black text-gray-950 dark:text-white mt-1">
              От идеи до готового изделия за 6 шагов
            </h2>
          </div>

          <div className="space-y-4">
            {steps.map((item) => (
              <div
                key={item.step}
                className="rounded-2xl border border-gray-200/90 dark:border-zinc-800/90 bg-white dark:bg-[#0E121E] p-6 sm:p-7 flex flex-col md:flex-row md:items-center justify-between gap-6 hover:border-[#55a9ef] transition-colors"
              >
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#0a3d90]/10 to-[#55a9ef]/20 text-[#0a3d90] dark:text-[#55a9ef] flex items-center justify-center font-mono font-black text-lg shrink-0">
                    {item.step}
                  </div>
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-bold text-gray-950 dark:text-white">
                        {item.title}
                      </h3>
                      <span className="text-xs font-mono px-2 py-0.5 rounded bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-zinc-400">
                        {item.duration}
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-gray-600 dark:text-zinc-300 leading-relaxed max-w-2xl">
                      {item.desc}
                    </p>
                  </div>
                </div>

                <div className="md:text-right shrink-0 border-t md:border-t-0 pt-3 md:pt-0 border-gray-100 dark:border-zinc-800/80">
                  <span className="text-[11px] text-gray-700 dark:text-zinc-400 block font-mono">
                    Результат этапа:
                  </span>
                  <span className="text-xs font-bold text-[#0a3d90] dark:text-[#55a9ef]">
                    {item.deliverable}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-16 sm:mt-20 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="rounded-3xl border border-gray-200/90 dark:border-zinc-800/90 bg-white dark:bg-[#0E121E] p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-pink-500/10 text-[#FF2E93] flex items-center justify-center">
                <CreditCard className="w-5 h-5" />
              </div>
              <div>
                <span className="text-xs font-mono font-bold uppercase text-[#FF2E93]">
                  Финансы
                </span>
                <h3 className="text-xl font-bold text-gray-950 dark:text-white">
                  Варианты оплаты
                </h3>
              </div>
            </div>

            <div className="space-y-4">
              {paymentMethods.map((pm) => (
                <div
                  key={pm.title}
                  className="p-4 rounded-xl border border-gray-100 dark:border-zinc-800 bg-gray-50/60 dark:bg-zinc-900/40"
                >
                  <h4 className="text-sm font-bold text-gray-950 dark:text-white mb-1">
                    {pm.title}
                  </h4>
                  <p className="text-xs text-gray-600 dark:text-zinc-400 leading-relaxed">
                    {pm.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-3xl border border-gray-200/90 dark:border-zinc-800/90 bg-white dark:bg-[#0E121E] p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-[#0a3d90] dark:text-[#55a9ef] flex items-center justify-center">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <span className="text-xs font-mono font-bold uppercase text-[#0a3d90] dark:text-[#55a9ef]">
                  Ответственность
                </span>
                <h3 className="text-xl font-bold text-gray-950 dark:text-white">
                  Гарантии и доставка
                </h3>
              </div>
            </div>

            <div className="space-y-4">
              {guarantees.map((g) => (
                <div
                  key={g.title}
                  className="p-4 rounded-xl border border-gray-100 dark:border-zinc-800 bg-gray-50/60 dark:bg-zinc-900/40"
                >
                  <h4 className="text-sm font-bold text-gray-950 dark:text-white mb-1">
                    {g.title}
                  </h4>
                  <p className="text-xs text-gray-600 dark:text-zinc-400 leading-relaxed">
                    {g.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {faqItems.length > 0 && (
          <section className="mt-16 sm:mt-20">
            <div className="max-w-xl mb-8">
              <span className="text-xs font-mono font-bold uppercase tracking-wider text-[#FF2E93]">
                FAQ
              </span>
              <h2 className="text-2xl sm:text-3xl font-black text-gray-950 dark:text-white mt-1">
                Частые вопросы по порядку работы
              </h2>
            </div>

            <div className="space-y-3 max-w-4xl">
              {faqItems.map((item, idx) => {
                const isOpen = openFaq === idx;
                return (
                  <div
                    key={item.q}
                    className="rounded-2xl border border-gray-200/90 dark:border-zinc-800/90 bg-white dark:bg-[#0E121E] overflow-hidden"
                  >
                    <button
                      type="button"
                      onClick={() => toggleFaq(idx)}
                      className="w-full flex items-center justify-between p-5 text-left text-sm sm:text-base font-bold text-gray-950 dark:text-white hover:text-[#0a3d90] dark:hover:text-[#55a9ef] transition-colors"
                    >
                      <span>{item.q}</span>
                      <ChevronDown
                        className={`w-4 h-4 shrink-0 text-gray-700 dark:text-zinc-400 transition-transform duration-200 ${
                          isOpen ? "rotate-180 text-[#0a3d90] dark:text-[#55a9ef]" : ""
                        }`}
                      />
                    </button>
                    {isOpen && (
                      <div className="px-5 pb-5 text-xs sm:text-sm text-gray-600 dark:text-zinc-300 leading-relaxed border-t border-gray-100 dark:border-zinc-800/80 pt-3">
                        {item.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

